'use client'
import React, { useRef, type ReactNode } from 'react'
import { useExposureDetection, type UseExposureDetectionOptions } from '../hooks/useExposureDetection.js'

export interface ExposureDetectorProps extends Omit<UseExposureDetectionOptions, 'onExposure'> {
  /**
   * 子元素
   */
  children: ReactNode
  /**
   * 曝光回调函数
   */
  onExposure?: () => void
  /**
   * 包装元素的标签，默认 'div'
   */
  as?: keyof JSX.IntrinsicElements
  /**
   * 包装元素的 className
   */
  className?: string
  /**
   * 包装元素的 style
   */
  style?: React.CSSProperties
}

/**
 * ExposureDetector - 曝光检测器
 *
 * @description 使用 IntersectionObserver 检测元素可见性的曝光检测组件，支持阈值和停留时间配置
 *
 * @example
 * ```tsx
 * <ExposureDetector
 *   exposureKey="my-component-1"
 *   threshold={0.5}
 *   duration={2000}
 *   onExposure={() => {
 *     console.log('元素已曝光')
 *   }}
 * >
 *   <div>内容</div>
 * </ExposureDetector>
 * ```
 */
export const ExposureDetector: React.FC<ExposureDetectorProps> = ({
  children,
  onExposure,
  exposureKey,
  threshold = 0.5,
  duration = 2000,
  once = true,
  rootMargin = '0px',
  as: Component = 'div',
  className,
  style,
  ...rest
}) => {
  const elementRef = useRef<HTMLElement>(null)

  useExposureDetection(elementRef, {
    exposureKey,
    threshold,
    duration,
    once,
    onExposure,
    rootMargin,
  })

  return React.createElement(
    Component,
    {
      ref: elementRef,
      className,
      style,
      ...rest,
    },
    children
  )
}

export default ExposureDetector
